import { memo, useCallback, useRef, useState } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@coreai/fluentui-react';
import {
  ChevronDownRegular,
  BrainCircuitRegular,
  WrenchRegular,
  DatabaseRegular,
  SettingsRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { useHandleLayout } from './useHandlePositions';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { useNodeValidationErrors } from '@/hooks/useProactiveValidation';
import { useWorkflowStore } from '@/stores/workflowStore';
import { ExecutionOverlay } from '@/components/shared';
import { ValidationErrorBadge } from './ValidationErrorBadge';
import { FloatingNode, useDetailLevel, type NodeStatus } from './FloatingNode';
import { useNodeCollapseStore } from '@/stores/nodeCollapseStore';
import { ottoTokens } from '@/theme/ottoTokens';
import type { AgentTool } from '@/types/workflowDefinition';
import {
  AgentVariantIcon,
  getAgentVariantFromTypes,
} from '../helpers/agentVariant';
import type { NodeExecutionStatus } from '@/types/monitoring';

/**
 * Agent capability types - extensible for future features
 */
export type AgentCapability = 'tools' | 'model' | 'memory';

export type FloatingAgentNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  // Agent model type (e.g., 'FoundryAgentService' for purple Foundry styling)
  agentModelType?: string;
  // Agent harness type (e.g., 'GHCP') — present only for Managed Agents,
  // drives the green styling + Code icon on the canvas.
  agentHarnessType?: string;
  // Tools
  tools?: Record<string, AgentTool>;
  onAddToolClick?: (agentName: string, anchorEl: HTMLElement) => void;
  // True while the tool-type chooser menu is anchored to this agent. Keeps
  // the Add tool button visible even after the cursor leaves the node so
  // the menu's anchor doesn't disappear out from under the user.
  isToolMenuOpen?: boolean;
  // Model
  modelDeployment?: string;
  connectionName?: string;
  onModelConfigClick?: (agentName: string) => void;
  // Memory (future-ready)
  memoryEnabled?: boolean;
  memoryType?: string;
  // Monitoring
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  iterationCount?: number;
  currentIteration?: number;
  onIterationChange?: (agentName: string, iteration: number) => void;
};

export type FloatingAgentNodeType = Node<FloatingAgentNodeData, 'floatingAgent'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  // Popover styles
  popoverSurface: {
    padding: tokens.spacingVerticalM,
    minWidth: '220px',
    maxWidth: '300px',
  },

  popoverHeader: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  popoverSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalS,
  },

  popoverDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },

  popoverSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },

  popoverRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },

  popoverKey: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  popoverValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '150px',
  },

  popoverHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    textAlign: 'center',
  },

  popoverActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },

  // Model handle - flows DOWN from agent (native variant, teal)
  handleModel: {
    bottom: '-8px !important',
    backgroundColor: `${ottoTokens.agentNativeBadgeBackground} !important`,
    border: `2px solid ${ottoTokens.agentNativeBadgeForeground} !important`,
    width: '10px !important',
    height: '10px !important',
    // HCM: !important on author colors doesn't block forced-colors substitution,
    // but explicit system-color overrides keep the handle visible + on-brand
    // (button-like control affordance) rather than relying on browser guesses.
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace !important',
      border: '2px solid ButtonText !important',
    },
  },
  // Tools handle - flows UP from agent (native variant, teal)
  handleTools: {
    top: '-8px !important',
    backgroundColor: `${ottoTokens.agentNativeBadgeBackground} !important`,
    border: `2px solid ${ottoTokens.agentNativeBadgeForeground} !important`,
    width: '10px !important',
    height: '10px !important',
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace !important',
      border: '2px solid ButtonText !important',
    },
  },
  // Foundry variant override — purple to match the agent body color
  handleFoundry: {
    backgroundColor: `${ottoTokens.agentFoundryBadgeBackground} !important`,
    border: `2px solid ${ottoTokens.agentFoundryBadgeForeground} !important`,
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace !important',
      border: '2px solid ButtonText !important',
    },
  },
  // Managed agent variant override — green to match the body color
  handleCoding: {
    backgroundColor: `${ottoTokens.agentCodingBadgeBackground} !important`,
    border: `2px solid ${ottoTokens.agentCodingBadgeForeground} !important`,
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace !important',
      border: '2px solid ButtonText !important',
    },
  },
  // Add tool button - centered on tools handle with hover reveal
  addToolButton: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1.5px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transitionProperty: 'opacity, background-color, border-color, transform',
    transitionDuration: tokens.durationFast,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      border: `1.5px solid ${tokens.colorBrandStroke1}`,
      transform: 'translateX(-50%) scale(1.1)',
    },
  },
  addToolButtonVisible: {
    opacity: 1,
  },
  addToolIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  // Iteration pager (for monitoring)
  iterationPager: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },

  iterationText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },

  iterationButton: {
    minWidth: 'auto',
    padding: tokens.spacingVerticalXXS,
    height: '16px',
    width: '16px',
  },
  modelTypeLabel: {
    fontWeight: tokens.fontWeightRegular,
    opacity: 0.65,
  },
  chevronRight: {
    transform: 'rotate(90deg)',
  },
  chevronLeft: {
    transform: 'rotate(-90deg)',
  },
});

/**
 * Floating-style Agent Node with scalable capabilities display.
 *
 * Design principles:
 * - Compact icon-based design with labels below
 * - Capability badges for tools, model, memory (and future features)
 * - Hover popover for full details
 * - Expandable tools view
 * - Zoom-based detail levels
 */
export const FloatingAgentNode = memo(
  ({ id, data, selected }: NodeProps<FloatingAgentNodeType>) => {
    const styles = useStyles();
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();
    const selectNodeByName = useWorkflowStore((state) => state.selectNodeByName);
    const [isHovered, setIsHovered] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    // The popover renders its own "Add Tool" button, but that button is
    // unmounted the moment we setPopoverOpen(false) — so using e.currentTarget
    // as the menu anchor leaves Fluent's Menu pointing at a detached DOM node
    // and it falls back to the top-left of the viewport. Anchoring to the
    // outer Add Tool button (always mounted on the agent) keeps the menu
    // positioned over the agent regardless of which trigger fired the menu.
    const addToolButtonRef = useRef<HTMLButtonElement>(null);

    // Tools
    const tools = data.tools ?? {};
    const toolCount = Object.keys(tools).length;
    const hasTools = toolCount > 0;
    // Read collapse state directly from the store (design-mode only) so the
    // add-tool button hides when the tool cluster is collapsed. The collapse
    // toggle itself now lives on the cluster edge / stub, not on this node.
    const isCollapsed = useNodeCollapseStore((s) => s.collapsedNodes.has(id));

    // Memory (future-ready)
    const hasMemory = data.memoryEnabled ?? false;

    // Connection status
    const { validationResult } = useNodeConnectionStatus({
      nodeId: id,
      operationType: 'Agent',
      connectionReferenceKey: data.connectionName,
    });

    // Get proactive validation errors for this node
    const { errors: validationErrors } = useNodeValidationErrors(data.actionName);

    // Monitoring mode
    const isMonitoringMode = data.executionStatus !== undefined;
    const iterationCount = data.iterationCount ?? 0;
    const currentIteration = data.currentIteration ?? 0;
    const hasMultipleIterations = iterationCount > 1;

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) {
        if (validationResult) {
          if (validationResult.status === 'mock-active') return 'mock';
          if (!validationResult.isValid) {
            return validationResult.status === 'warning' ? 'warning' : 'error';
          }
        }
        return 'default';
      }

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        case 'pending':
          return 'default';
        default:
          return 'default';
      }
    };

    // Handlers
    const handleConfigureClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        // Select this node to open the parameter panel
        selectNodeByName(data.actionName, 'action');
      },
      [data.actionName, selectNodeByName]
    );

    const handleAddToolClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setPopoverOpen(false);
        const anchor = addToolButtonRef.current ?? e.currentTarget;
        data.onAddToolClick?.(data.actionName, anchor);
      },
      [data]
    );

    const handlePrevIteration = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIteration > 0) {
          data.onIterationChange?.(data.actionName, currentIteration - 1);
        }
      },
      [data, currentIteration]
    );

    const handleNextIteration = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIteration < iterationCount - 1) {
          data.onIterationChange?.(data.actionName, currentIteration + 1);
        }
      },
      [data, currentIteration, iterationCount]
    );

    // Check if connection is missing or invalid
    const hasConnectionError = validationResult && !validationResult.isValid;
    const agentVariant = getAgentVariantFromTypes(data.agentModelType, data.agentHarnessType);
    const isFoundryAgent = agentVariant === 'foundry';
    const isCodingAgent = agentVariant === 'managed';

    // Build status message for error/mock badge tooltip
    const statusMessage =
      validationResult?.status === 'mock-active'
        ? 'Using test data'
        : validationResult && !validationResult.isValid
          ? validationResult.error?.message ?? 'Connection not configured'
          : undefined;

    return (
      <div
        className={styles.wrapper}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Hover popover for quick info */}
        <Popover
          positioning="above-start"
          openOnHover
          mouseLeaveDelay={300}
          open={popoverOpen}
          onOpenChange={(_, dataEv) => setPopoverOpen(dataEv.open)}
        >
          <PopoverTrigger disableButtonEnhancement>
            <div>
              <FloatingNode
                icon={<AgentVariantIcon variant={agentVariant} />}
                label={data.label}
                detailLevel={detailLevel}
                selected={selected}
                status={getNodeStatus()}
                statusMessage={statusMessage}
                shape="hexagon"
                accentColor={hasConnectionError ? 'neutral' : `agent-${agentVariant}`}
                ariaLabel={`${data.label} agent node${hasConnectionError ? ' - connection error' : ''}`}
              >
                <Handle
                  type="target"
                  position={handles.input.position}
                  id="input"
                  isConnectable={true}
                  style={handles.input.style}
                />
                <Handle
                  type="source"
                  position={handles.output.position}
                  id="output"
                  isConnectable={true}
                  style={handles.output.style}
                />
              </FloatingNode>
            </div>
          </PopoverTrigger>

          <PopoverSurface className={styles.popoverSurface}>
            <div className={styles.popoverHeader}>
              {data.label}
              {isCodingAgent ? (
                <span className={styles.modelTypeLabel}> (Managed Agent)</span>
              ) : data.agentModelType && (
                <span className={styles.modelTypeLabel}>
                  {' '}({data.agentModelType === 'FoundryAgentService' ? 'Foundry'
                    : data.agentModelType === 'FoundryAgentServiceV2' ? 'Foundry V2'
                    : data.agentModelType === 'AzureOpenAI' ? 'Azure OpenAI'
                    : data.agentModelType === 'APIMGenAIGateway' ? 'APIM'
                    : data.agentModelType === 'V1ChatCompletionsService' ? 'OpenAI'
                    : data.agentModelType})
                </span>
              )}
            </div>
            <div className={styles.popoverDivider} />

            <div className={styles.popoverSection}>
              {/* Model info */}
              <div className={styles.popoverRow}>
                <span className={styles.popoverKey}>
                  <BrainCircuitRegular /> Model
                </span>
                <span className={styles.popoverValue} title={data.modelDeployment}>
                  {data.modelDeployment ?? 'Not configured'}
                </span>
              </div>

              {/* Connection info */}
              {data.connectionName && (
                <div className={styles.popoverRow}>
                  <span className={styles.popoverKey}>Connection</span>
                  <span className={styles.popoverValue} title={data.connectionName}>
                    {data.connectionName}
                  </span>
                </div>
              )}

              {/* Tools info */}
              <div className={styles.popoverRow}>
                <span className={styles.popoverKey}>
                  <WrenchRegular /> Tools
                </span>
                <span className={styles.popoverValue}>
                  {toolCount > 0 ? `${toolCount} configured` : 'None'}
                </span>
              </div>

              {/* Memory info (future-ready) */}
              {hasMemory && (
                <div className={styles.popoverRow}>
                  <span className={styles.popoverKey}>
                    <DatabaseRegular /> Memory
                  </span>
                  <span className={styles.popoverValue}>
                    {data.memoryType ?? 'Enabled'}
                  </span>
                </div>
              )}

              {/* Execution duration in monitoring mode */}
              {data.executionDuration !== undefined && (
                <div className={styles.popoverRow}>
                  <span className={styles.popoverKey}>Duration</span>
                  <span className={styles.popoverValue}>{data.executionDuration}ms</span>
                </div>
              )}
            </div>

            {/* Action buttons (not in monitoring mode) */}
            {!isMonitoringMode && (
              <>
                <div className={styles.popoverDivider} />
                <div className={styles.popoverActions}>
                  <Button
                    size="small"
                    icon={<SettingsRegular />}
                    onClick={handleConfigureClick}
                  >
                    Configure
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<AddRegular />}
                    onClick={handleAddToolClick}
                  >
                    Add Tool
                  </Button>
                </div>
              </>
            )}
          </PopoverSurface>
        </Popover>

        {/* Model handle - flows DOWN from agent (always visible) */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="model"
          isConnectable={false}
          className={mergeClasses(
            styles.handleModel,
            isFoundryAgent && styles.handleFoundry,
            isCodingAgent && styles.handleCoding,
          )}
          aria-label="Model connection"
        />

        {/* Tools handle - flows UP from agent (always visible for connection) */}
        <Handle
          type="source"
          position={Position.Top}
          id="tools"
          isConnectable={false}
          className={mergeClasses(
            styles.handleTools,
            isFoundryAgent && styles.handleFoundry,
            isCodingAgent && styles.handleCoding,
          )}
          style={{ opacity: hasTools ? 1 : 0.3 }}
          aria-label={hasTools ? `${toolCount} tools connected` : 'No tools'}
        />

        {/* Add Tool button - centered on tools handle, shown on hover */}
        {!isMonitoringMode && !isCollapsed && data.onAddToolClick && (
          <Button
            ref={addToolButtonRef as unknown as React.Ref<never>}
            className={mergeClasses(
              styles.addToolButton,
              (isHovered || data.isToolMenuOpen) && styles.addToolButtonVisible
            )}
            appearance="subtle"
            icon={<AddRegular className={styles.addToolIcon} />}
            onClick={handleAddToolClick}
            aria-label="Add tool"
            title="Add tool"
          />
        )}

        {/* Iteration pager for monitoring mode */}
        {isMonitoringMode && hasMultipleIterations && (
          <div className={styles.iterationPager}>
            <Button
              className={styles.iterationButton}
              appearance="subtle"
              size="small"
              icon={<ChevronDownRegular className={styles.chevronRight} />}
              onClick={handlePrevIteration}
              disabled={currentIteration === 0}
              aria-label="Previous iteration"
            />
            <span className={styles.iterationText}>
              {currentIteration + 1}/{iterationCount}
            </span>
            <Button
              className={styles.iterationButton}
              appearance="subtle"
              size="small"
              icon={<ChevronDownRegular className={styles.chevronLeft} />}
              onClick={handleNextIteration}
              disabled={currentIteration >= iterationCount - 1}
              aria-label="Next iteration"
            />
          </div>
        )}

        {/* Execution overlay */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
          />
        )}

        {/* Show validation error badge (only in design mode) */}
        {!isMonitoringMode && validationErrors.length > 0 && (
          <ValidationErrorBadge
            nodeName={data.actionName}
            errors={validationErrors}
            position="top-left"
            size="small"
          />
        )}
      </div>
    );
  }
);

FloatingAgentNode.displayName = 'FloatingAgentNode';
